import { getToken } from "../../../../shared/data/storage/authStorage";
import {
  ApprovalCountsResponseDTO,
  ApprovalsResponseDTO,
  GetApprovalsParams,
  ApproveReviewRequestDTO,
  RejectReviewRequestDTO,
  ApprovalActionResponseDTO,
} from "../models/ApprovalDTOs";
import { API_BASE_URL } from "../../../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

/**
 * Get approval counts by entity type and status
 * GET /api/approvals/counts?mode=approvals (default) or mode=my-requests
 */
export const getApprovalCounts = async (
  mode: "approvals" | "my-requests" = "approvals",
): Promise<ApprovalCountsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/approvals/counts?mode=${mode}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Get paginated approvals with filters
 * GET /api/approvals
 */
export const getApprovals = async (
  params: GetApprovalsParams = {},
): Promise<ApprovalsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();

  // Handle both camelCase (from domain) and snake_case (from DTOs)
  const entityType = (params as any).entityType || params.entity_type;
  if (entityType) {
    queryParams.append("entity_type", entityType);
  }

  if (params.status) {
    queryParams.append("status", params.status);
  }

  if (params.limit !== undefined) {
    queryParams.append("limit", params.limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }

  if (params.search) {
    queryParams.append("search", params.search);
  }

  const url = `${API_BASE_URL}/api/approvals?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Approve a review
 * POST /api/approvals/{id}/approve
 */
export const approveReview = async (
  reviewId: number,
  comments?: string,
): Promise<ApprovalActionResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/approvals/${reviewId}/approve`;

  const body: ApproveReviewRequestDTO = {};
  if (comments) {
    body.comments = comments;
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Reject a review
 * POST /api/approvals/{id}/reject
 */
export const rejectReview = async (
  reviewId: number,
  comments: string,
): Promise<ApprovalActionResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/approvals/${reviewId}/reject`;

  const body: RejectReviewRequestDTO = {
    comments,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Get month-end checklist reviews with filters and pagination
 * GET /api/month-end/reviews?status=pending|approved|rejected&limit=20&offset=0
 */
export const getMonthEndChecklistReviews = async (
  params: {
    status?: "pending" | "approved" | "rejected";
    limit?: number;
    offset?: number;
  } = {},
): Promise<ApprovalsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();

  if (params.status) {
    queryParams.append("status", params.status);
  }

  if (params.limit !== undefined) {
    queryParams.append("limit", params.limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }

  const url = `${API_BASE_URL}/api/month-end/reviews?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Approve a month-end checklist review
 * POST /api/month-end/reviews/{id}/approve
 */
export const approveMonthEndChecklistReview = async (
  reviewId: number,
  comments?: string,
): Promise<ApprovalActionResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/reviews/${reviewId}/approve`;

  const body: ApproveReviewRequestDTO = {};
  if (comments) {
    body.comments = comments;
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Reject a month-end checklist review
 * POST /api/month-end/reviews/{id}/reject
 */
export const rejectMonthEndChecklistReview = async (
  reviewId: number,
  comments: string,
): Promise<ApprovalActionResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const url = `${API_BASE_URL}/api/month-end/reviews/${reviewId}/reject`;

  const body: RejectReviewRequestDTO = {
    comments,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Get my review requests (reviews I requested) with pagination
 * GET /api/reviews/my-requests
 */
export const getMyRequests = async (
  params: {
    entityType?: string;
    limit?: number;
    offset?: number;
    search?: string;
  } = {},
): Promise<ApprovalsResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();

  if (params.entityType) {
    queryParams.append("entity_type", params.entityType);
  }

  if (params.limit !== undefined) {
    queryParams.append("limit", params.limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }

  if (params.search) {
    queryParams.append("search", params.search);
  }

  const url = `${API_BASE_URL}/api/reviews/my-requests?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
